/**
 * The columns each view type may be pointed at.
 *
 * Every one of these lists is the backend's own rule restated, because the menu
 * that offers a column is the only thing that decides what can ever be stored
 * -- and a column the backend would refuse is a column nobody should be able to
 * pick. Each function says which rule it mirrors.
 *
 * The dates are next door in `date-fields.ts`, which carries a pairing rule as
 * well and so is worth keeping separate.
 */
import type { Field, FieldTypeName } from '@/api/types'

/**
 * A column whose first image can be a card's cover.
 *
 * Mirrors `can_represent_files`: a file column, or a formula-backed one whose
 * result is a single file.
 */
export function representsFiles(field: Field): boolean {
  return (
    field.type === 'file' ||
    (field as { formula_type?: string }).formula_type === 'single_file'
  )
}

/** The columns offered as a card cover, in the table's order. */
export function fileFields(fields: readonly Field[]): Field[] {
  return fields.filter(representsFiles)
}

/**
 * The columns a board can stack by.
 *
 * A single select and nothing else: `BoardViewType.prepare_values` checks the
 * stored field is a `SingleSelectField`, and the stacks are its options.
 */
export function singleSelectFields(fields: readonly Field[]): Field[] {
  return fields.filter((field) => field.type === 'single_select')
}

/**
 * The columns that can supply a list view's lists.
 *
 * `ListViewType`'s `LIST_FIELD_TYPES`: a link row, a single select or a
 * multiple select -- the three that hold a set of things a row belongs to.
 */
const LIST_FIELD_TYPES: readonly FieldTypeName[] = [
  'link_row',
  'single_select',
  'multiple_select',
]

export function listFields(fields: readonly Field[]): Field[] {
  return fields.filter((field) => LIST_FIELD_TYPES.includes(field.type))
}

/**
 * The columns of a linked table that can be displayed through the link.
 *
 * Mirrors `can_be_target_of_adhoc_lookup`, which `LinkRowFieldType` is the only
 * type to turn off -- the value arrives through the join the view's own rows
 * endpoint makes, and a link row cannot be carried through a second one.
 */
export function targetFields(fields: readonly Field[]): Field[] {
  return fields.filter((field) => field.type !== 'link_row')
}
